import { LinearProgress, linearProgressClasses, Stack, Typography, useTheme } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { YantraExecutionInfo } from "../../tanstack/queries/player";
import { useMemo, useState } from "react";
import { useTranslate } from "../../hooks/useTranslate";
import Big from "big.js";
import { getActiveExecutionId, setStatus } from "../../stores/statusStore/statusStore";
import { useWebSocket, YantraStatus } from "../../hooks/useWebSocket";
import { useStatusStore } from "../../stores/statusStore/useStatusStore";
import BackButton from "./BackButton";
import HistoryBtn from "../history/HistoryBtn";

export default function StatusLine({ name }: { name: string }) {
  const statusStore = useStatusStore();
  const queryClient = useQueryClient();
  const theme = useTheme();
  const executionData = queryClient.getQueryData<{
    yantraExecution: YantraExecutionInfo;
  }>(["execution", statusStore.activeExecutionId]);
  const { t } = useTranslate();
  const statusTranslationKey = useMemo(() => {
    switch (statusStore.status) {
      default:
        return "waiting";
      case "PLACE_BETS":
        return "placeYourBets";
      case "COLLECTING_BETS":
        return "collectingBets";
      case "CALCULATED":
        return "winningResult";
    }
  }, [statusStore.status]);

  const stopToStartDiffSeconds = useMemo(() => {
    return Math.floor(((executionData?.yantraExecution.stopBetsDate || 0) - (executionData?.yantraExecution.startDate || 0)) / 1000);
  }, [executionData?.yantraExecution]);

  const calculateToStopDiffSeconds = useMemo(() => {
    return Math.floor(((executionData?.yantraExecution.calculateDate || 0) - (executionData?.yantraExecution.stopBetsDate || 0)) / 1000);
  }, [executionData?.yantraExecution]);

  const restartToCalculateDiffSeconds = useMemo(() => {
    return Math.floor(((executionData?.yantraExecution.restartDate || 0) - (executionData?.yantraExecution.calculateDate || 0)) / 1000);
  }, [executionData?.yantraExecution]);

  const currentSectionFullTime = useMemo(() => {
    switch (statusStore.status) {
      default:
        return 1;
      case "PLACE_BETS":
        return Math.max(stopToStartDiffSeconds, 1);
      case "COLLECTING_BETS":
        return Math.max(calculateToStopDiffSeconds, 1);
      case "CALCULATED":
        return Math.max(restartToCalculateDiffSeconds, 1);
    }
  }, [statusStore.status, executionData]);

  const currentSectionEndTime = useMemo(() => {
    switch (statusStore.status) {
      default:
        return Date.now();
      case "PLACE_BETS":
        return executionData?.yantraExecution.stopBetsDate || Date.now();
      case "COLLECTING_BETS":
        return executionData?.yantraExecution.calculateDate || Date.now();
      case "CALCULATED":
        return executionData?.yantraExecution.restartDate || Date.now();
    }
  }, [statusStore.status, executionData]);

  const [timeLeft, setTimeLeft] = useState(Math.floor((currentSectionEndTime - Date.now()) / 1000));

  useWebSocket({
    onExecutionTimer: (yantraExecutionId: string, newStatus: YantraStatus, timeLeft: number) => {
      if (yantraExecutionId === getActiveExecutionId()) {
        setTimeLeft(timeLeft);
        setStatus(newStatus);
      }
    },
  });

  const percentage = useMemo(() => {
    return timeLeft > 0 ? new Big(timeLeft).div(currentSectionFullTime).mul(100).round(0, Big.roundHalfEven).toNumber() : 0;
  }, [timeLeft, currentSectionFullTime]);

  return (
    <Stack
      useFlexGap
      direction={"row"}
      justifyContent={"space-between"}
      alignItems={"end"}
      spacing={5}
      sx={{
        width: "100%",
      }}
    >
      <Stack useFlexGap direction={"row"} alignItems={"center"} justifyContent={"start"}>
        <BackButton />
        <Typography
          sx={{
            color: "#FFFFFF",
            fontSize: theme.typography.pxToRem(16),
            lineHeight: theme.typography.pxToRem(24),
            fontWeight: 400,
          }}
        >
          {name ?? ""}
        </Typography>
      </Stack>
      <Stack useFlexGap spacing={0.125} direction={"column"} alignItems={"center"} sx={{ width: "100%" }}>
        <Typography
          sx={{
            color: "#FFFFFF",
            fontSize: theme.typography.pxToRem(14),
            fontWeight: 400,
            lineHeight: theme.typography.pxToRem(16),
            alignSelf: "center",
          }}
        >
          {t(`statuses.${statusTranslationKey}`)} {timeLeft > 0 ? `(${timeLeft})` : ""}
        </Typography>
        <LinearProgress
          variant={percentage > 0 ? "determinate" : "indeterminate"}
          value={percentage}
          sx={{
            display: "flex",
            alignSelf: "stretch",
            height: theme.spacing(1),
            borderRadius: theme.spacing(0.5),
            bgcolor: "#47A6F4",
            [`& .${linearProgressClasses.bar}`]: {
              bgcolor: "#29F778",
            },
          }}
        />
      </Stack>
      <HistoryBtn />
    </Stack>
  );
}
